import { XbotPageContext, XbotStarterExample, XbotStarters } from "@dto/pyxbot-agent.dto";
import { FC, ReactNode, useEffect, useEffectEvent, useMemo, useState } from "react";
import {
  LuActivity, LuBell, LuChartColumn, LuGauge, LuGitCompare, LuHash, LuHeartPulse, LuList, LuMessageCircle, LuNetwork,
  LuSearch, LuSlidersHorizontal, LuTrendingUp,
} from "react-icons/lu";
import { useAppGlobStore } from "src/appGlobalStore";
import { Label } from "src/data/Label";
import { GLOB } from "src/util/Glob";
import { loadXbotStarters } from "../agent/chatClient";
import { builtFromEstate, CAPABILITIES_QUESTION, estateFromMenu, StarterQuestion, starterQuestions } from "./starterQuestions";

// One icon per question kind; anything unlisted falls back to a plain speech bubble.
const KIND_ICONS: Readonly<Record<string, ReactNode>> = {
  count: <LuHash />,
  rank: <LuTrendingUp />,
  list: <LuList />,
  trend: <LuActivity />,
  status: <LuHeartPulse />,
  health: <LuHeartPulse />,
  relation: <LuNetwork />,
  relations: <LuNetwork />,
  value: <LuGauge />,
  alerts: <LuBell />,
  configuration: <LuSlidersHorizontal />,
  find: <LuSearch />,
  compare: <LuGitCompare />,
  spread: <LuChartColumn />,
};

function iconOf(key: string): ReactNode {
  return Object.prototype.hasOwnProperty.call(KIND_ICONS, key) ? KIND_ICONS[key] : <LuMessageCircle />;
}

export type StarterItem = Pick<XbotStarterExample, "key" | "label">;

/** The questions about the page's object, under the heading that names it. */
export interface StarterAbout {
  title: string;
  questions: readonly StarterItem[];
}

export interface StarterPromptsProps {
  questions: readonly StarterItem[];
  /** True when the questions were built from this installation, false for the neutral ones. */
  fromEstate: boolean;
  /** "About esxi-04.dr.lab.local:" and the questions about that object, drawn first; absent or null for none. */
  about?: StarterAbout | null;
  onPick: (question: string) => void;
}

/** The examples of the welcome, from what the service answered or, until it has, from the window's own. */
export type StarterSet = Pick<StarterPromptsProps, "questions" | "fromEstate"> & { about: StarterAbout | null };

// Prefers the service's own questions (with an About heading) over the static fallback; no heading is guessed.
export function starterSet(starters: XbotStarters | null | undefined, fallback: readonly StarterQuestion[]): StarterSet {
  const own = { about: null, questions: fallback, fromEstate: builtFromEstate(fallback) };
  if (!starters) {
    return own;
  }
  const name = starters.about?.name?.trim() ?? "";
  const aboutQuestions = starters.about && name ? starters.page_examples : [];
  return {
    about: aboutQuestions.length ? { title: `About ${name}:`, questions: aboutQuestions } : null,
    questions: starters.examples.length ? starters.examples : own.questions,
    fromEstate: starters.examples.length ? true : own.fromEstate,
  };
}

/** A list of example questions; a pick sends the question's own words. */
function starterList(title: string, questions: readonly StarterItem[], onPick: (question: string) => void): ReactNode {
  // Native buttons, so Tab and Enter reach every question.
  return <div key={title} className="xm-chat-starters-group">
    <h5 className="xm-chat-starters-title">{title}</h5>
    <ul className="xm-chat-starters-list">
      {questions.map((one, index) => <li key={`${one.key}-${index}`} className="xm-chat-starters-cell">
        <button type="button" className="xm-chat-starters-item" onClick={() => onPick(one.label)}>
          <span className="xm-chat-starters-item-icon" aria-hidden="true">{iconOf(one.key)}</span>
          <span className="xm-chat-starters-item-label">{one.label}</span>
        </button>
      </li>)}
    </ul>
  </div>;
}

/** The welcome: who answers, from what, and that nothing is changed (Xbot only reads). */
export const WELCOME_TITLE = `Hi, I'm ${Label.XORBOT}`;
export const WELCOME_TEXT = "Ask about anything XorMon monitors. I read its data and its manual, and I change nothing.";
export const WELCOME_LINK = "See what I can do";

// The welcome itself (no greeting bubble): who answers and from what, its "what can I do" a link in the sentence,
// then the page's object's questions and the installation's after them. Styling comes from antd theme tokens.
export const StarterPrompts: FC<StarterPromptsProps> = ({ questions, fromEstate, about, onPick }) => {
  const general = about ? "Or about your whole installation:" : fromEstate ? "Try a question about your installation:" : "Try one of these:";
  return <div className="xm-chat-starters">
    <div className="xm-chat-welcome">
      <h4 className="xm-chat-welcome-title">{WELCOME_TITLE}</h4>
      <p className="xm-chat-welcome-text">
        {WELCOME_TEXT}{" "}
        <button type="button" className="xm-chat-welcome-link" onClick={() => onPick(CAPABILITIES_QUESTION)}>{WELCOME_LINK}</button>
      </p>
    </div>
    {about ? starterList(about.title, about.questions, onPick) : null}
    {starterList(general, questions, onPick)}
  </div>;
};

/** Falls back to menu-tree-built examples (starterQuestions.ts) until the service answers or if it can't. */
export const AIChatStarters: FC<{
  onPick: (question: string) => void;
  pageContext?: XbotPageContext | null;
  /** The conversation's thread, which the first message goes to. */
  threadId?: string | null;
  onStarters?: (starters: XbotStarters, pageContext: XbotPageContext | null | undefined) => void;
}> = ({ onPick, pageContext, threadId, onStarters }) => {
  const topMenu = useAppGlobStore((state) => state.topMenu);

  // Storage device list fills in the same pass that publishes topMenu, so re-read it then too.
  const estate = useMemo(() => estateFromMenu(topMenu, GLOB.menuService.storagesBy.device.children), [topMenu]);
  const fallback = useMemo(() => starterQuestions(estate), [estate]);

  /** The service's answer, with the page it was asked for: an answer for another page is not this page's. */
  const [answered, setAnswered] = useState<{ page: XbotPageContext | null | undefined; starters: XbotStarters } | null>(null);
  const heard = useEffectEvent((starters: XbotStarters, page: XbotPageContext | null | undefined) => {
    setAnswered({ page, starters });
    onStarters?.(starters, page);
  });

  // The thread is read when asking, not watched: it is named as soon as the person starts typing, and the examples
  // for this page must not be asked for a second time then.
  const currentThread = useEffectEvent(() => threadId);

  useEffect(() => {
    const aborter = new AbortController();
    void loadXbotStarters(pageContext, currentThread(), aborter.signal).then((starters) => {
      if (starters && !aborter.signal.aborted) {
        heard(starters, pageContext);
      }
    });
    return () => aborter.abort();
  }, [pageContext]);

  const starters = answered?.page === pageContext ? answered?.starters ?? null : null;
  return <StarterPrompts {...starterSet(starters, fallback)} onPick={onPick} />;
};
